/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventContext, EventErrorContext } from '@okta/okta-signin-widget';
import { Subscription, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY } from 'src/app/constants/url-constants';
import { AuthService } from 'src/app/services/auth.service';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';
import { environment } from 'src/environments/environment';
import {
  IdentityApi,
  InteractionsApi,
  LoggerDatadogService,
  NotifyService,
  OktaService,
  detectAdblock,
  isBehindProxy
} from 'sustainment-component';
import { Organization } from 'sustainment-component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  public errorMsg = '';
  public isLoggingIn = false;
  public focusEmail = false;
  public focusPassword = false;
  public loading = true;
  /** true -> custom /signup page; false -> Okta widget's inline signup. */
  public useCustomSignup = environment.useCustomSignup ?? true;

  private _returnUrl: string;
  private _subscriptions = new Subscription();
  private _inviteCode = '';
  private _loginHintEmail = '';

  public constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _identityApi: IdentityApi,
    private _oktaService: OktaService,
    private _logService: LoggerDatadogService,
    private _authService: AuthService,
    private _userAccountQuery: UserAccountQuery,
    private interactionsApi: InteractionsApi,
    private notifyService: NotifyService
  ) {
    _oktaService.widget.on(
      'afterError',
      function (context: EventContext, error: EventErrorContext) {
        _logService.info(JSON.stringify(error), context as never);
      }
    );
  }

  public ngOnInit(): void {
    this._returnUrl = this._route.snapshot.queryParamMap.get('src') || '/';

    const urlObject = new URL(this._returnUrl, window.location.origin);
    this._loginHintEmail = urlObject.searchParams.get('login_hint') || '';

    if (this._loginHintEmail) {
      urlObject.searchParams.delete('login_hint');
      this._returnUrl = urlObject.pathname + urlObject.search + urlObject.hash;
    }

    // Check if the 'alreadyRegistered' parameter is present in the URL
    const alreadyRegistered =
      this._route.snapshot.queryParamMap.has('alreadyRegistered');

    if (alreadyRegistered) {
      this.notifyService.showInfo(
        'Company already registered. Please login to continue.'
      );
    }

    // Save the invite code if present, so we can use it after authentication
    this._inviteCode = this._route.snapshot.queryParamMap.get('ref') || '';

    if (this._inviteCode && this._route.snapshot.url[1]?.path === 'register') {
      this.loading = true;
      this._subscriptions.add(
        this.interactionsApi.getOdysseus(this._inviteCode).subscribe((res) => {
          if (!res.stillValid) {
            window.location.href = '/login?alreadyRegistered=true';
          }
        })
      );
    }

    const customLoginRedirectPage = this._route.snapshot.queryParamMap.get(
      CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY
    );

    if (customLoginRedirectPage) {
      sessionStorage.setItem(
        CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY,
        customLoginRedirectPage
      );
    }

    //Redirect out of login when getting a valid appKey
    this._subscriptions.add(
      this._userAccountQuery.userSession$
        .pipe(first((session) => !!session?.appKey))
        .subscribe(() => {
          const refCode = this._route.snapshot.queryParamMap.get('state');
          if (refCode && refCode !== 'a') {
            this._subscriptions.add(
              this._identityApi.setUserCode(refCode).subscribe()
            );
          }

          // Check if we have an invite code, and if so, add it to the redirect URL
          let redirectUrl = this._returnUrl;
          if (this._inviteCode) {
            // If return URL already has query params, append the ref parameter
            if (redirectUrl.includes('?')) {
              redirectUrl += `&ref=${this._inviteCode}`;
            } else {
              redirectUrl += `?ref=${this._inviteCode}`;
            }
          }

          this._router.navigate([redirectUrl]);
        })
    );

    //If the user has a pending organization redirect to pending screen
    this._subscriptions.add(
      this._userAccountQuery.userAccount$
        .pipe(
          first(
            (account) =>
              !!account?.organizations.length &&
              (account.organizations[0] as Organization & { pending: boolean })
                .pending
          )
        )
        .subscribe((account) =>
          this._router.navigateByUrl('/pending-activation', {
            state: { companyName: account.organizations[0].name }
          })
        )
    );

    this.initializeOkta();
  }

  private initializeOkta(): void {
    combineLatest([
      this._authService.isAuthenticated$,
      this._authService.loadingSession$
    ]).subscribe(([isAuthenticated, loadingSession]) => {
      if (!loadingSession && !isAuthenticated) {
        this.loading = false; // show the login form
      }
    });

    this._oktaService.widget.on('afterRender', (ev: EventContext) => {
      if (ev.controller === 'primary-auth' || ev.formName === 'identify') {
        this.prefillLoginHintEmail();
      }
    });

    if (this._oktaService.widget.authClient.isLoginRedirect()) {
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get('error');

      if (error === 'interaction_required') {
        // Per Okta docs: re-show the widget so it can continue the IDP flow
        this.showLogin();
      } else if(error === "access_denied") { 
        this._oktaService.widget.authClient.signInWithRedirect({
          originalUri: this.getReturnUrl(),
          state: 'a',
          nonce: 'b',
          idp: environment.okta.googleId,
        });
      } else if (error) {
        this.loading = false;
        this.errorMsg = urlParams.get('error_description') || 'Authorization error';
        this._authService.closeSession();
        this.showLogin();
      } else {
        this._returnUrl = this.getReturnUrl();
  
        this._oktaService.widget.authClient.token
          .parseFromUrl()
          .then((res) => {
            if (res.tokens) {
              this._authService.login(res.tokens);
            }
          })
          .catch(() => {
            this._router.navigate([this._returnUrl]);
          });
      }
    } else {
      this.showLogin();
    }
  }

  private showLogin(): void {
    this._oktaService.widget
      .showSignIn({ el: '#okta-signin-container' })
      .then((response) => {
        if (response.status === 'SUCCESS' && response.tokens) {
          this._authService.login(response.tokens, true);

          const email = response.tokens?.idToken?.claims?.email;

          if (email) {
            this.checkAdblock(email);
            this.checkProxy(email);
          }
        }
      });
  }

  public ngOnDestroy(): void {
    this._oktaService.widget.remove();
    this._subscriptions.unsubscribe();
  }

  private getReturnUrl(): string {
    const defaultUrl = this._route.snapshot.queryParamMap.get('src') || '/';

    const originalUri = this._oktaService.widget.authClient.getOriginalUri();

    if (originalUri && originalUri.indexOf('?src=') >= 0) {
      const search = decodeURIComponent(
        originalUri?.substring(originalUri.indexOf('?src='), originalUri.length)
      ).replace('?src=', '');

      return search;
    }

    return defaultUrl;
  }

  private prefillLoginHintEmail(): void {
    if (!this._loginHintEmail) return;

    const emailInput = document.querySelector(
      'input[name="identifier"]'
    ) as HTMLInputElement;

    if (!emailInput) return;

    emailInput.value = this._loginHintEmail;
    emailInput.dispatchEvent(new Event('input', { bubbles: true }));
    emailInput.dispatchEvent(new Event('change', { bubbles: true }));

    const passwordInput = document.querySelector(
      'input[name="password"]'
    ) as HTMLInputElement;

    if (passwordInput) passwordInput.focus();
  }

  private checkAdblock(email: string): void {
    detectAdblock().then((hasAdblock) => {
      if (hasAdblock) {
        const message = `PortalApp: ${email} is using adblocker`;

        this._logService.info(message);
        this._identityApi.sendLog(message).subscribe();
      }
    });
  }

  private checkProxy(email: string): void {
    if (isBehindProxy()) {
      const message = `PortalApp: ${email} is behind proxy`;

      this._logService.info(message);
      this._identityApi.sendLog(message).subscribe();
    }
  }
}
